<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-location-dot"></i> Citywise Metadata Management</h1>
          <p class="admin-subtitle">Generate and manage per-city SEO metadata records.</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <!-- Citywise Metadata Generation -->
      <div class="admin-card admin-section">
        <button @click="toggleCitywiseSection" class="section-header">
          <span class="chevron" :class="{ expanded: showCitywiseSection }">▶</span>
          Generate Citywise Metadata (Bulk)
        </button>
        <div v-show="showCitywiseSection" class="section-content">
          <button @click="generateCitywiseMetadata" class="admin-btn admin-btn--primary" :disabled="isGenerating">
            <span v-if="isGenerating">Generating...</span>
            <span v-else>Generate All Citywise Metadata</span>
          </button>
          <p class="admin-muted info">
            This will create metadata for all city/subtype combinations (e.g. /find-a-room/new-york-city).
          </p>
        </div>
      </div>

      <!-- Edit Form -->
      <div v-if="isEditing" class="admin-card admin-section">
        <h2 class="admin-card__title">Edit Citywise Metadata</h2>
        <form @submit.prevent="saveCitywiseMetadata">
          <div class="admin-field">
            <label class="admin-label">Page Name</label>
            <input class="admin-input" v-model="editForm.pageName" type="text" disabled />
          </div>
          <div class="admin-field">
            <label class="admin-label">Meta Tags (JSON Format)</label>
            <textarea class="admin-textarea" v-model="editForm.metaTagsJson" rows="8"></textarea>
            <small class="admin-muted">Enter valid JSON</small>
          </div>
          <div class="admin-field">
            <label class="admin-label">Description (optional)</label>
            <input class="admin-input" v-model="editForm.description" type="text" />
          </div>
          <div class="admin-field">
            <label class="admin-label">JSON-LD Schema (optional)</label>
            <textarea class="admin-textarea" v-model="editForm.jsonLd" rows="6"></textarea>
          </div>
          <div class="admin-toolbar">
            <button type="submit" class="admin-btn admin-btn--primary">Update</button>
            <button type="button" @click="resetEditForm" class="admin-btn admin-btn--outline">Cancel</button>
          </div>
        </form>
      </div>
      <!-- Move edit form above the list for visibility -->
      <!-- Citywise Metadata List -->
      <div class="admin-section">
        <h2 class="admin-card__title list-title">Citywise Metadata Records</h2>
        <p v-if="totalCount > 0" class="admin-muted records-count">
          Showing {{ citywiseList.length }} of {{ totalCount }} records.
        </p>
        <div v-if="citywiseList.length === 0 && !isLoading" class="admin-empty">
          <i class="fa-solid fa-inbox"></i>
          <p class="admin-empty__title">No citywise metadata found</p>
          <p>Generate or refresh.</p>
        </div>
        <div v-for="item in citywiseList" :key="item._id" class="admin-card metadata-card">
          <div class="card-header">
            <h3>{{ item.pageName }}</h3>
            <small class="admin-muted">{{ item.description }}</small>
          </div>
          <div class="card-content">
            <pre>{{ JSON.stringify(item.metaTags, null, 2) }}</pre>
            <div v-if="item.jsonLd">
              <label class="admin-label jsonld-label">JSON-LD Schema:</label>
              <pre>{{ item.jsonLd }}</pre>
            </div>
          </div>
          <div class="card-actions">
            <button @click="editCitywiseMetadata(item)" class="admin-btn admin-btn--outline admin-btn--sm">Edit</button>
            <button @click="deleteCitywiseMetadata(item.pageName)" class="admin-btn admin-btn--danger admin-btn--sm">Delete</button>
          </div>
        </div>

        <div v-if="hasMore" class="show-more-wrap">
          <button @click="loadMore" class="admin-btn admin-btn--outline" :disabled="isLoading">
            <span v-if="isLoading">Loading...</span>
            <span v-else>Show More ({{ totalCount - citywiseList.length }} remaining)</span>
          </button>
        </div>

        <!-- Edit Form -->
        <div v-if="isEditing" class="admin-card admin-section">
          <h2 class="admin-card__title">Edit Citywise Metadata</h2>
          <form @submit.prevent="saveCitywiseMetadata">
            <div class="admin-field">
              <label class="admin-label">Page Name</label>
              <input class="admin-input" v-model="editForm.pageName" type="text" disabled />
            </div>
            <div class="admin-field">
              <label class="admin-label">Meta Tags (JSON Format)</label>
              <textarea class="admin-textarea" v-model="editForm.metaTagsJson" rows="8"></textarea>
              <small class="admin-muted">Enter valid JSON</small>
            </div>
            <div class="admin-field">
              <label class="admin-label">Description (optional)</label>
              <input class="admin-input" v-model="editForm.description" type="text" />
            </div>
            <div class="admin-field">
              <label class="admin-label">JSON-LD Schema (optional)</label>
              <textarea class="admin-textarea" v-model="editForm.jsonLd" rows="6"></textarea>
            </div>
            <div class="admin-toolbar">
              <button type="submit" class="admin-btn admin-btn--primary">Update</button>
              <button type="button" @click="resetEditForm" class="admin-btn admin-btn--outline">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const isEditing = ref(false);
const editForm = ref({
  pageName: '',
  metaTagsJson: '',
  description: '',
  jsonLd: '',
});

const editCitywiseMetadata = (item) => {
  console.log('Edit button clicked:', item); // Debug log
  isEditing.value = true;
  editForm.value = {
    pageName: item.pageName,
    metaTagsJson: JSON.stringify(item.metaTags, null, 2),
    description: item.description || '',
    jsonLd: item.jsonLd || '',
  };
};

const resetEditForm = () => {
  isEditing.value = false;
  editForm.value = {
    pageName: '',
    metaTagsJson: '',
    description: '',
    jsonLd: '',
  };
};

const injectMetaTags = (metaTags) => {
  // Remove previously injected tags
  document
    .querySelectorAll("meta[data-citywise='true']")
    .forEach(el => el.remove());

  if (!metaTags) return;

  Object.entries(metaTags).forEach(([key, value]) => {
    if (!value) return;

    const meta = document.createElement("meta");

    if (key.startsWith("og:")) {
      meta.setAttribute("property", key);
    } else {
      meta.setAttribute("name", key);
    }

    meta.setAttribute("content", value);
    meta.setAttribute("data-citywise", "true");

    document.head.appendChild(meta);
  });

  // Also update title
  if (metaTags.title) {
    document.title = metaTags.title;
  }
};

const saveCitywiseMetadata = async () => {
  try {
    let metaTags;
    try {
      metaTags = JSON.parse(editForm.value.metaTagsJson);
    } catch (e) {
      toast.error('Invalid JSON in meta tags: ' + e.message);
      return;
    }
    const payload = {
      pageName: editForm.value.pageName,
      metaTags,
      description: editForm.value.description,
      jsonLd: editForm.value.jsonLd ? editForm.value.jsonLd : undefined,
    };
    await axiosInst.post('/api/citywise-metadata/upsert', payload);
    toast.success('Citywise metadata updated successfully!');
    resetEditForm();
    fetchCitywiseMetadata();
  } catch (error) {
    toast.error('Failed to update citywise metadata: ' + error.message);
  }
};

const deleteCitywiseMetadata = async (pageName) => {
  if (!confirm(`Are you sure you want to delete citywise metadata for ${pageName}?`)) return;
  try {
    await axiosInst.delete(`/api/citywise-metadata/${pageName}`);
    toast.success(`Citywise metadata for ${pageName} deleted successfully!`);
    fetchCitywiseMetadata();
  } catch (error) {
    toast.error('Failed to delete citywise metadata: ' + error.message);
  }
};
import { ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import axiosInst from "@/utils/axiosinstance";
import { toast } from "vue-sonner";

const router = useRouter();
const citywiseList = ref([]);
const showCitywiseSection = ref(false);
const isGenerating = ref(false);

// Pagination state — load 100 records at a time via "Show More"
const PAGE_SIZE = 100;
const currentPage = ref(0);
const totalCount = ref(0);
const hasMore = ref(false);
const isLoading = ref(false);

const toggleCitywiseSection = () => {
  showCitywiseSection.value = !showCitywiseSection.value;
};

const goToDashboard = () => {
  router.push("/");
};

const generateCitywiseMetadata = async () => {
  isGenerating.value = true;
  try {
    const response = await axiosInst.post("/api/citywise-metadata/generate");
    toast.success(response.data.message || "Citywise metadata generated!");
    fetchCitywiseMetadata();
  } catch (error) {
    toast.error("Failed to generate citywise metadata: " + error.message);
  } finally {
    isGenerating.value = false;
  }
};

// Fetch a single page. reset=true starts over from page 1 (used on mount,
// after generate/save/delete); reset=false appends the next page ("Show More").
const fetchCitywiseMetadata = async (reset = true) => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    const nextPage = reset ? 1 : currentPage.value + 1;
    const response = await axiosInst.get("/api/citywise-metadata", {
      params: { page: nextPage, limit: PAGE_SIZE },
    });
    const batch = response.data.data || [];
    if (reset) {
      citywiseList.value = batch;
    } else {
      citywiseList.value = citywiseList.value.concat(batch);
    }
    currentPage.value = nextPage;
    totalCount.value = response.data.total ?? citywiseList.value.length;
    hasMore.value =
      response.data.hasMore ?? citywiseList.value.length < totalCount.value;
  } catch (error) {
    toast.error("Failed to fetch citywise metadata: " + error.message);
  } finally {
    isLoading.value = false;
  }
};

const loadMore = () => fetchCitywiseMetadata(false);

onMounted(() => {
  fetchCitywiseMetadata();
});

// Inject JSON-LD into document head for SEO
const injectJsonLd = (jsonLd) => {
  const oldScript = document.getElementById("citywise-jsonld");
  if (oldScript) oldScript.remove();
  if (jsonLd) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "citywise-jsonld";
    script.text = jsonLd;
    document.head.appendChild(script);
  }
};

// watch(citywiseList, () => {
//   if (citywiseList.value.length > 0 && citywiseList.value[0].jsonLd) {
//     injectJsonLd(citywiseList.value[0].jsonLd);
//   }
// });
watch(citywiseList, () => {
  if (citywiseList.value.length > 0) {
    const item = citywiseList.value[0];

    if (item.metaTags) {
      injectMetaTags(item.metaTags);
    }

    if (item.jsonLd) {
      injectJsonLd(item.jsonLd);
    }
  }
});
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */
.admin-field { margin-bottom: 14px; }

.list-title { margin-bottom: 12px; }

/* Collapsible section header */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0.5rem;
  margin: -0.5rem 0 0 -0.5rem;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--g-text);
  transition: background-color 0.2s ease;
  border-radius: var(--g-radius-sm);
}
.section-header:hover {
  background: var(--g-hover);
}
.chevron {
  display: inline-block;
  transition: transform 0.3s ease;
  font-size: 0.85rem;
  color: var(--g-blue);
}
.chevron.expanded {
  transform: rotate(90deg);
}
.section-content {
  padding-top: 14px;
}
.info {
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

/* Metadata record cards */
.metadata-card {
  margin-bottom: 12px;
  padding: 0;
  overflow: hidden;
}
.card-header {
  background: var(--g-surface-2);
  padding: 11px 14px;
  border-bottom: 1px solid var(--g-border);
}
.card-header h3 {
  margin: 0 0 2px 0;
  color: var(--g-blue);
  font-size: 14.5px;
  font-weight: 600;
}
.card-content {
  padding: 14px;
  background: var(--g-surface);
}
.card-content pre {
  background: var(--g-surface-2);
  padding: 12px;
  border-radius: var(--g-radius-sm);
  overflow-x: auto;
  font-size: 0.85rem;
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
}
.jsonld-label {
  margin-top: 1rem;
  display: block;
}
.card-actions {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--g-divider);
}
.records-count {
  margin-bottom: 12px;
}
.show-more-wrap {
  text-align: center;
  margin: 16px 0;
}
</style>
